/**
 * widgetRenderers — per-type widget rendering (Phase 5, 2026-05-16).
 *
 * Każdy widget ma swój renderer + ikona + label + default config.
 * Renderer dostaje { widget, data } gdzie `data` to response z API endpoint
 * /api/dashboard/widgets/data/{type}.
 */

import React from 'react';
import { Badge } from '../ui/badge';
import {
  TrendingUp, Activity, FileText, Zap, Layers, Brain,
  BarChart3, DollarSign,
} from 'lucide-react';


function fmtPLN(v) {
  if (!v || v === 0) return '0 PLN';
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} mld PLN`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} mln PLN`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}k PLN`;
  return `${Math.round(v)} PLN`;
}

function fmtNum(v) {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('pl-PL').format(v);
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}


const STAGE_LABELS = {
  discovered: 'Wykryte',
  researched: 'Zbadane',
  contacted: 'Kontakt',
  spec_in: 'Spec-in',
  tracked: 'Tracked',
  won: 'Wygrane',
  lost: 'Przegrane',
  unknown: 'Inne',
};

const STAGE_COLORS = {
  discovered: 'bg-zinc-500',
  researched: 'bg-blue-500',
  contacted: 'bg-cyan-500',
  spec_in: 'bg-primary',
  tracked: 'bg-amber-500',
  won: 'bg-emerald-500',
  lost: 'bg-red-500',
};


// ---- Renderers per widget type --------------------------------------------

function KpiStageCounts({ data }) {
  const items = data?.items || [];
  const total = items.reduce((acc, x) => acc + (x.count || 0), 0);
  return (
    <div className="space-y-1.5">
      <div className="text-2xl font-bold">{fmtNum(total)}</div>
      <div className="text-[10px] text-muted-foreground mb-1">inwestycji łącznie</div>
      <div className="space-y-1">
        {items.map(it => (
          <div key={it.stage} className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${STAGE_COLORS[it.stage] || 'bg-zinc-500'}`} />
            <span className="text-[11px] flex-1 truncate">{STAGE_LABELS[it.stage] || it.stage}</span>
            <span className="text-[11px] font-medium">{fmtNum(it.count)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function KpiTotalValue({ data }) {
  return (
    <div className="space-y-1">
      <div className="text-2xl font-bold">{fmtPLN(data?.total_value || 0)}</div>
      <div className="text-[10px] text-muted-foreground">aktywny pipeline</div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{fmtNum(data?.count)} inwestycji</span>
        <span>śr. {fmtPLN(data?.avg_value || 0)}</span>
      </div>
    </div>
  );
}


function ActivityFeedWidget({ data }) {
  const items = data?.items || [];
  if (items.length === 0) {
    return <div className="text-[11px] text-muted-foreground text-center py-4">Brak ostatnich zdarzeń</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.slice(0, 15).map(evt => (
        <div key={evt.id} className="flex items-center gap-1.5 text-[11px]">
          <div className="w-1 h-1 rounded-full bg-primary shrink-0" />
          <span className="font-medium truncate">{evt.event_type}</span>
          <span className="text-muted-foreground truncate">{evt.item_id}</span>
          <span className="text-muted-foreground/70 ml-auto text-[10px] shrink-0">{fmtTime(evt.created_at)}</span>
        </div>
      ))}
    </div>
  );
}


function ActivityStatsWidget({ data }) {
  const byType = data?.by_event_type || [];
  const total = data?.total || 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-bold">{fmtNum(total)}</div>
        <span className="text-[10px] text-muted-foreground">zdarzeń · {data?.days_back || 7}d</span>
      </div>
      <div className="space-y-1">
        {byType.slice(0, 6).map(it => (
          <div key={it.event_type} className="flex items-center justify-between text-[11px]">
            <span className="truncate">{it.event_type}</span>
            <span className="font-medium">{fmtNum(it.count)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function RecentSpecsWidget({ data }) {
  const items = data?.items || [];
  if (items.length === 0) {
    return <div className="text-[11px] text-muted-foreground text-center py-4">Brak ostatnich spec-inów</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.map(evt => (
        <div key={evt.id} className="text-[11px] border-b border-border/40 pb-1.5 last:border-0">
          <div className="font-medium truncate">{evt.payload?.product_name || evt.payload?.product_id || '—'}</div>
          <div className="text-muted-foreground text-[10px] flex justify-between">
            <span className="truncate">{evt.item_id}</span>
            <span className="ml-2 shrink-0">{fmtTime(evt.created_at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}


function AutomationRunsWidget({ data }) {
  const items = data?.items || [];
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-bold">{fmtNum(data?.total_runs || 0)}</div>
        <span className="text-[10px] text-muted-foreground">uruchomień · {items.length} reguł</span>
      </div>
      <div className="space-y-1">
        {items.slice(0, 6).map(r => (
          <div key={r.id} className="flex items-center justify-between text-[11px]">
            <span className="truncate flex-1">{r.name}</span>
            <Badge variant="outline" className="text-[9px] h-3.5 px-1 ml-1">{r.run_count}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}


function TopPatternsWidget({ data }) {
  const items = data?.items || [];
  if (items.length === 0) {
    return <div className="text-[11px] text-muted-foreground text-center py-4">🧠 Adam jeszcze nie ma patternów</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.slice(0, 10).map(p => (
        <div key={`${p.pattern_type}-${p.key}`} className="text-[11px] border-b border-border/40 pb-1.5 last:border-0">
          <div className="flex items-center justify-between">
            <span className="font-medium truncate">{p.key}</span>
            <Badge variant="outline" className="text-[9px] h-3.5 px-1 ml-1">×{p.count}</Badge>
          </div>
          <div className="text-muted-foreground text-[10px]">
            {p.pattern_type} · confidence {Math.round((p.confidence || 0) * 100)}%
          </div>
        </div>
      ))}
    </div>
  );
}


function KanbanMiniWidget({ data }) {
  const byStage = data?.by_stage || {};
  const stages = Object.keys(byStage);
  if (stages.length === 0) {
    return <div className="text-[11px] text-muted-foreground text-center py-4">Brak inwestycji</div>;
  }
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${stages.length}, 1fr)` }}>
      {stages.map(s => (
        <div key={s} className="min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <div className={`w-1.5 h-1.5 rounded-full ${STAGE_COLORS[s] || 'bg-zinc-500'}`} />
            <span className="text-[10px] font-semibold truncate">{STAGE_LABELS[s] || s}</span>
            <Badge variant="outline" className="text-[9px] h-3.5 px-1 ml-auto">{byStage[s].length}</Badge>
          </div>
          <div className="space-y-1">
            {byStage[s].slice(0, 4).map(inv => (
              <div key={inv.source_id} className="bg-muted/30 rounded p-1 text-[10px]">
                <div className="font-medium truncate">{inv.title || inv.source_id}</div>
                {inv.city && <div className="text-muted-foreground truncate">{inv.city}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ---- Public registry -----------------------------------------------------

export const WIDGET_TYPES = {
  kpi_stage_counts: {
    label: '📊 Pipeline (etapy)',
    icon: BarChart3,
    default_position: { x: 0, y: 99, w: 6, h: 3 },
    default_config: {},
    Renderer: KpiStageCounts,
  },
  kpi_total_value: {
    label: '💰 Wartość pipeline',
    icon: DollarSign,
    default_position: { x: 0, y: 99, w: 6, h: 2 },
    default_config: {},
    Renderer: KpiTotalValue,
  },
  activity_feed: {
    label: '📡 Ostatnia aktywność',
    icon: Activity,
    default_position: { x: 0, y: 99, w: 6, h: 5 },
    default_config: { limit: 15 },
    Renderer: ActivityFeedWidget,
  },
  activity_stats: {
    label: '📈 Statystyki tygodnia',
    icon: TrendingUp,
    default_position: { x: 6, y: 99, w: 6, h: 5 },
    default_config: { days_back: 7 },
    Renderer: ActivityStatsWidget,
  },
  recent_specs: {
    label: '📐 Ostatnie spec-iny',
    icon: FileText,
    default_position: { x: 0, y: 99, w: 6, h: 4 },
    default_config: { limit: 10 },
    Renderer: RecentSpecsWidget,
  },
  automation_runs: {
    label: '⚡ Automation runs',
    icon: Zap,
    default_position: { x: 6, y: 99, w: 6, h: 4 },
    default_config: { days_back: 7 },
    Renderer: AutomationRunsWidget,
  },
  top_patterns: {
    label: '🧠 Top patterns (Adam)',
    icon: Brain,
    default_position: { x: 0, y: 99, w: 6, h: 5 },
    default_config: { limit: 10 },
    Renderer: TopPatternsWidget,
  },
  kanban_mini: {
    label: '🗂 Kanban skrót',
    icon: Layers,
    default_position: { x: 0, y: 99, w: 12, h: 5 },
    default_config: { limit: 8 },
    Renderer: KanbanMiniWidget,
  },
};


export function WidgetRenderer({ widget, data }) {
  const def = WIDGET_TYPES[widget.widget_type];
  if (!def?.Renderer) {
    return <div className="text-[11px] text-muted-foreground">Nieznany typ: {widget.widget_type}</div>;
  }
  const Comp = def.Renderer;
  return <Comp widget={widget} data={data} />;
}
